import { Component, inject, Injector, OnInit } from '@angular/core';
import { Database, get, ref } from '@angular/fire/database';
import { AuthService } from 'src/app/services/auth.service';
import { BaseComponent } from 'src/app/shared/baseComponent';
import * as ScenarioDefs from '../vscenarioedit/ScenarioClasses';

interface MatrixRow {
  className: string;
  counts: number[];
}

@Component({
  selector: 'app-interactivity-matrix',
  templateUrl: './interactivity-matrix.component.html',
  styleUrls: ['./interactivity-matrix.component.css']
})
export class InteractivityMatrixComponent extends BaseComponent implements OnInit {
  database = inject(Database);
  isAuthenticatedWithFirebase = false;
  scenarios: any[] = [];
  interactivityClasses: string[] = [];
  matrixRows: MatrixRow[] = [];
  filteredScenarioIndex: number | null = null;

  constructor(injector: Injector, private authService: AuthService) {
    super(injector);
  }

  async ngOnInit(): Promise<void> {
    this.interactivityClasses = this.getInteractivityClasses();
    this.isAuthenticatedWithFirebase = await this.authService.initializeAuth(() => this.onSuccessfulLogin());
  }

  async signInWithGoogle(): Promise<void> {
    this.isAuthenticatedWithFirebase = await this.authService.signInWithGoogle();
    if (this.isAuthenticatedWithFirebase) {
      await this.onSuccessfulLogin();
    }
  }

  private async onSuccessfulLogin(): Promise<void> {
    await this.getData();
  }

  toggleScenarioFilter(scenarioIndex: number): void {
    this.filteredScenarioIndex = this.filteredScenarioIndex === scenarioIndex
      ? null
      : scenarioIndex;
  }

  clearScenarioFilter(): void {
    this.filteredScenarioIndex = null;
  }

  isRowVisible(row: MatrixRow): boolean {
    return this.filteredScenarioIndex === null || row.counts[this.filteredScenarioIndex] > 0;
  }

  async getData(): Promise<void> {
    const scenarioRef = ref(this.database, 'scenarios');
    const snapshot = await get(scenarioRef);
    if (!snapshot.exists()) {
      this.scenarios = [];
      this.matrixRows = [];
      return;
    }

    this.scenarios = BaseComponent.toArray(snapshot.val())
      .filter((scenario: any) => scenario.isVersion2 === true)
      .sort((first: any, second: any) => (first.seqNo ?? 0) - (second.seqNo ?? 0));
    const observedClasses = this.scenarios
      .flatMap((scenario: any) => BaseComponent.toArray(scenario.interactivities || []))
      .map((interactivity: any) => interactivity.class)
      .filter((className: any) => className && className !== 'Interactivity');
    this.interactivityClasses = Array.from(new Set([...this.interactivityClasses, ...observedClasses]))
      .sort((first, second) => first.localeCompare(second));
    this.buildMatrixRows();
  }

  private getInteractivityClasses(): string[] {
    const base = (ScenarioDefs as any).InteractivityModel;
    const classes = new Set<string>();

    for (const [exportName, value] of Object.entries(ScenarioDefs)) {
      if (exportName === 'InteractivityModel' || typeof value !== 'function') {
        continue;
      }
      const constructor = value as any;
      if (!base || !(constructor.prototype instanceof base)) {
        continue;
      }

      const model = new constructor();
      const inheritedClass = model.class === 'Interactivity';
      const className = inheritedClass
        ? exportName.replace(/Model$/, '')
        : model.class;
      if (className && className !== 'Interactivity') {
        classes.add(className);
      }
    }

    return Array.from(classes).sort((first, second) => first.localeCompare(second));
  }

  private buildMatrixRows(): void {
    const lowSequenceScenarios = this.scenarios.filter((scenario) => Number(scenario.seqNo ?? 0) < 1000);
    const rows = this.interactivityClasses.map((className) => ({
      className,
      counts: this.scenarios.map((scenario) => {
        const interactivities = BaseComponent.toArray(scenario.interactivities || []);
        return interactivities.filter((interactivity: any) => interactivity.class === className).length;
      })
    }));

    this.matrixRows = rows.sort((first, second) => {
      const firstHasLowSequenceOccurrence = lowSequenceScenarios.some((scenario) =>
        BaseComponent.toArray(scenario.interactivities || [])
          .some((interactivity: any) => interactivity.class === first.className)
      );
      const secondHasLowSequenceOccurrence = lowSequenceScenarios.some((scenario) =>
        BaseComponent.toArray(scenario.interactivities || [])
          .some((interactivity: any) => interactivity.class === second.className)
      );

      if (firstHasLowSequenceOccurrence !== secondHasLowSequenceOccurrence) {
        return firstHasLowSequenceOccurrence ? -1 : 1;
      }
      return first.className.localeCompare(second.className);
    });
  }
}
